import React, { useState, useMemo } from 'react';
import { useClimbingData } from '../hooks/useClimbingData.js';
import { useChartData } from '../hooks/useChartData.js';
import { EmptyState } from './EmptyState.js';
import ChartGridLayout from './ChartGridLayout.js';
import SingleChartLayout from './SingleChartLayout.js';
import CompactHeader from './CompactHeader.js';
import GradeModal from './GradeModal.js';
import { MetricsGrid } from './MetricsGrid.js';
import { convertToVGrade } from '../utils/gradeUtils.js';

const ClimbingAnalysis = ({
  data,
  onDataLoaded,
  hideUpload = false,
  climberName = null,
  lockedStyle = null,
  lockedGradeSystem = null,
  lockedYear = null,
  lockedMonth = null,  // specific month 1-12 or null/'all'
  lockedMode = null,  // 'poster', 'dashboard', or null
  singleChart = null,  // Single chart embed: 'grades', 'flash', 'progression', etc.
  singleChartHeading = null  // Optional heading override for single chart mode
}) => {
  const [processingErrors, setProcessingErrors] = useState([]);
  // Individual chart states instead of array
// Change these useState lines:
const [chart0, setChart0] = useState('ascents');          // Top left
const [chart1, setChart1] = useState('grades');           // Top right
const [chart2, setChart2] = useState('routesPerYear');    // Bottom left
const [chart3, setChart3] = useState('crags');            // Bottom right
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedGradeData, setSelectedGradeData] = useState(null);
const [chartLayouts, setChartLayouts] = useState({
  grades: true,  // or true if you want horizontal by default
  flash: false,
  progression: false,
  ascents: false,
  routesPerYear: false,
  crags: false,
  types: false,
  projects: false,
  cragPerformance: false,
  heatmap: false,
  cgpCge: false
});

  // Custom hooks for data management
  const {
    gradeSystem,
    setGradeSystem,
    selectedYear,
    setSelectedYear,
    selectedCrag,
    setSelectedCrag,
    selectedCountry,
    setSelectedCountry,
    climbingStyle,
    setClimbingStyle,
    filteredData,
    metrics,
    years,
    crags,
    countries
  } = useClimbingData(data, {
    initialStyle: lockedStyle || 'sport',
    initialGradeSystem: lockedGradeSystem || 'ewbank',
    initialYear: lockedYear || 'all',
    initialMonth: lockedMonth || 'all'
  });
  
    const [isPosterMode, setIsPosterMode] = useState(lockedMode === 'poster');

  // Combine individual chart states into array for compatibility
  const selectedCharts = useMemo(() => 
    [chart0, chart1, chart2, chart3],
    [chart0, chart1, chart2, chart3]
  );

  const handleLayoutChange = (metric, isHorizontal) => {
    setChartLayouts(prev => ({
      ...prev,
      [metric]: isHorizontal
    }));
  };

  const handleChartChange = (position, metric) => {
    switch (position) {
      case 0:
        setChart0(metric);
        break;
      case 1:
        setChart1(metric);
        break;
      case 2:
        setChart2(metric);
        break;
      case 3:
        setChart3(metric);
        break;
      default:
        break;
    }
  };

  const handleGradeClick = (data) => {
    if (!data?.activePayload?.[0]) return;
    
    const clickedData = data.activePayload[0].payload;
    const grade = clickedData.grade;
    
    const routes = filteredData?.filter(ascent => {
      if (climbingStyle === 'boulder') {
        return convertToVGrade(ascent["Route Grade"]) === Number(grade);
      }
      const roundedGrade = Math.round(ascent.ewbanks);
      return roundedGrade === Number(grade);
    });

    if (routes?.length) {
      setSelectedGradeData({
        grade,
        routes: routes.sort((a, b) => new Date(b["Ascent Date"]) - new Date(a["Ascent Date"]))
      });
      setIsModalOpen(true);
    }
  };

  const handleSearch = (searchTerm) => {
    // Implement search functionality if needed
    console.log('Search:', searchTerm);
  };
  
  React.useEffect(() => {
    if (data?.length > 0 && data[0]?._processingErrors) {
      setProcessingErrors(data[0]._processingErrors);
    }
  }, [data]);

  // Handle data loading
  const handleDataLoaded = (newData) => {
    // Extract errors if present
    if (newData?.length > 0 && newData[0]?._processingErrors) {
      setProcessingErrors(newData[0]._processingErrors);
    }
    onDataLoaded(newData);
  };


  if (!data?.length) {
    return <EmptyState onDataLoaded={onDataLoaded} />;
  }

  // Single chart embed mode - render just one chart full-width
  if (singleChart) {
    return (
      <SingleChartLayout
        chart={singleChart}
        filteredData={filteredData}
        climbingStyle={climbingStyle}
        gradeSystem={gradeSystem}
        onGradeClick={handleGradeClick}
        chartLayouts={chartLayouts}
        climberName={climberName}
        selectedYear={selectedYear}
        headingOverride={singleChartHeading}
      />
    );
  }

  const metricsConfig = [
    ['grades', 'Grade Distribution'],
    ['flash', 'Flash Probability'],
    ['progression', 'Grade Progression'],
    ['ascents', 'Ascent History'],
    ['routesPerYear', 'Cumulative Routes'],
    ['crags', 'Top Crags'],
    ['types', 'Ascent Types'],
    ['projects', 'Projects'],
    ['cragPerformance', 'Crag Performance'],
    ['bestClimbs', 'Best Climbs'],
    ['partners', 'Climbing Partners'],
    ['heatmap', 'Heatmap'],
    ['cgpCge', 'CGP & CGE'],
    ['cgpPerGrade', 'CGP/day by Grade'],
  ];

  return (
    <div className="w-full space-y-4">
      {!isPosterMode && (
        <>
          <div className="flex justify-between items-center gap-4">
            <CompactHeader
              onDataLoaded={handleDataLoaded}
              data={data}
              processingErrors={processingErrors}
              setProcessingErrors={setProcessingErrors}
              hideUpload={hideUpload}
              climberName={climberName}
              lockedStyle={lockedStyle}
              lockedGradeSystem={lockedGradeSystem}
              lockedYear={lockedYear}
              filters={{
                selectedYear,
                selectedCrag,
                selectedCountry,
                years,
                crags,
                countries,
                climbingStyle,
                selectedCharts,
                gradeSystem
              }}
              onFiltersChange={{
                onYearChange: setSelectedYear,
                onCragChange: setSelectedCrag,
                onCountryChange: setSelectedCountry,
                onStyleChange: setClimbingStyle,
                onGradeSystemChange: setGradeSystem
              }}
              onSearch={handleSearch}
            />
            {!lockedMode && (
              <button
                onClick={() => setIsPosterMode(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
              >
                Switch to Poster Mode
              </button>
            )}
          </div>

          {metrics && (
            <MetricsGrid 
              metrics={metrics} 
              gradeSystem={gradeSystem} 
              climbingStyle={climbingStyle} 
            />
          )}
        </>
      )}

      {isPosterMode && !lockedMode && (
        <div className="fixed top-4 right-4 z-50">
          <button
            onClick={() => setIsPosterMode(false)}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
          >
            Exit Poster Mode
          </button>
        </div>
      )}

<ChartGridLayout
  metrics={metricsConfig}
  selectedCharts={selectedCharts}
  onChartChange={handleChartChange}
  filteredData={filteredData}
  climbingStyle={climbingStyle}
  gradeSystem={gradeSystem}
  onGradeClick={handleGradeClick}
  chartLayouts={chartLayouts}
  onLayoutChange={handleLayoutChange}
  isPosterMode={isPosterMode}
  lockedMode={lockedMode}
  onExitPosterMode={() => setIsPosterMode(false)}
  climberName={climberName}
  selectedYear={selectedYear}
/>
      <GradeModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        gradeData={selectedGradeData}
        gradeSystem={gradeSystem}
        climbingStyle={climbingStyle}
      />
    </div>
  );
};

export default ClimbingAnalysis;